import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { X, Plus, Save } from 'lucide-react';

/**
 * Host-configurable "How did you hear about this event?" source options.
 *
 * - Always shows a read-only "Other" pill at the end (guaranteed present on save)
 * - Host can add, remove, reorder(simple up/down not included — keep minimal), save
 * - "Save as default" button updates host_defaults.source_options via PUT
 * - `onChange(nextArray)` is called with the current event-scoped list
 *
 * Safe generic default (used if the host has no saved default and the event
 * has no source_options yet) is defined in lib/sourceOptions.js.
 */

export default function SourceOptionsSection({ value = null, onChange, onSaveAsDefault }) {
  const [draft, setDraft] = useState('');
  // value=null means "not configured" — show the safe default list as the
  // starting point so the host sees what guests will see.
  const list = Array.isArray(value) && value.length > 0 ? value : DEFAULT_OPTIONS;

  const addOption = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    if (trimmed.toLowerCase() === 'other') {
      setDraft('');
      return;
    }
    if (list.some((o) => o.toLowerCase() === trimmed.toLowerCase())) {
      setDraft('');
      return;
    }
    // Keep Other at the end (guaranteed). Strip & re-append so host can add
    // options in their preferred order.
    const withoutOther = list.filter((o) => o.toLowerCase() !== 'other');
    onChange([...withoutOther, trimmed, 'Other']);
    setDraft('');
  };

  const removeOption = (opt) => {
    if (opt.toLowerCase() === 'other') return;
    const next = list.filter((o) => o !== opt);
    // Ensure Other is always present at the end.
    if (!next.some((o) => o.toLowerCase() === 'other')) next.push('Other');
    onChange(next);
  };

  return (
    <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-6" data-testid="source-options-section">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-[#EAE7E1]">Guest Source Options</h2>
          <p className="text-sm text-[#777] mt-1">These options appear when guests are asked how they heard about your event.</p>
        </div>
        {onSaveAsDefault && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onSaveAsDefault(list)}
            className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#D4AF37] rounded-sm shrink-0"
            data-testid="save-source-options-default-btn"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />Save as default
          </Button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="source-options-list">
        {list.map((opt) => {
          const isOther = opt.toLowerCase() === 'other';
          return (
            <span
              key={opt}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs ${isOther ? 'border border-[#1A1A1A] bg-[#050505] text-[#777]' : 'border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#EAE7E1]'}`}
              data-testid={`source-option-chip-${opt.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {opt}
              {!isOther && (
                <button
                  type="button"
                  aria-label={`Remove ${opt}`}
                  onClick={() => removeOption(opt)}
                  className="text-[#777] hover:text-[#C44545]"
                  data-testid={`remove-source-option-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          );
        })}
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="Add an option (e.g., Friend, Instagram)"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); addOption(); }
          }}
          className="flex-1"
          data-testid="source-option-input"
        />
        <Button
          type="button"
          onClick={addOption}
          className="bg-[#1A1A1A] hover:bg-[#222] text-[#EAE7E1] rounded-sm"
          data-testid="add-source-option-btn"
        >
          <Plus className="w-4 h-4 mr-1" />Add
        </Button>
      </div>

      <p className="text-[11px] text-[#555] mt-3">
        <span className="text-[#AFAFAF]">Other</span> is always included. Options are deduplicated automatically.
      </p>
    </div>
  );
}

export const DEFAULT_OPTIONS = [
  'Friend',
  'Social Media',
  'Website',
  'Email / Newsletter',
  'Flyer / QR Code',
  'Other',
];
